import { convertAbiToInterface, createSafeContract, getAccountTokenBalance, getBatchBalancesWithFallback, getTokenUri, getTokenOfOwnerByIndex } from '@explorins/web3-ts/ethers';
import { g as globalCacheService, C as CacheTTL } from './index--OssIds0.js';
import { a as Web3ChainService, W as Web3ChainApi } from './web3-chain-service-BWRmwmmJ.js';
import '@explorins/web3-types';
import { T as TenantManager } from './tenant-manager-xmYKBFGu.js';
import { NativeTokenTypes } from '@explorins/pers-shared';

// type ContractAnalysis = ReturnType<ContractDomainService['analyzeContract']>;
/**
 * TokenDomainService - Clean, focused token operations
 *
 * Caching strategy:
 * - Balance/ownership: NO CACHING - always fetch fresh from blockchain
 *   (event-based updates in consuming apps handle refresh)
 * - Metadata: CACHED 24h - IPFS content is immutable
 */
class TokenDomainService {
    constructor(web3Api, metadataService, contractService) {
        this.web3Api = web3Api;
        this.metadataService = metadataService;
        this.contractService = contractService;
        this.cache = globalCacheService.createNamespace('token-domain');
    }
    async getTokenBalance(params) {
        const abi = convertAbiToInterface(params.abi);
        const balance = await this.web3Api.getTokenBalance({ ...params, abi });
        return {
            tokenId: params.tokenId,
            balance,
            hasBalance: balance > 0,
            metadata: null
        };
    }
    async getTokenMetadata(params) {
        const cacheKey = `metadata:${params.contractAddress}:${params.tokenId}:${params.chainId}`;
        return this.cache.getOrSet(cacheKey, async () => {
            try {
                const abi = convertAbiToInterface(params.abi);
                const tokenUri = await this.web3Api.getTokenUri({ ...params, abi });
                const metadata = tokenUri
                    ? await this.metadataService.fetchAndProcessMetadata(tokenUri)
                    : null;
                return { tokenId: params.tokenId, tokenUri, metadata };
            }
            catch (error) {
                console.error('Error fetching token metadata:', error);
                return { tokenId: params.tokenId, tokenUri: null, metadata: null };
            }
        }, CacheTTL.METADATA);
    }
    async getTokenWithBalance(params) {
        const [balanceResult, metadataResult] = await Promise.all([
            this.getTokenBalance({ ...params, tokenId: params.tokenId }),
            this.getTokenMetadata(params)
        ]);
        return {
            ...balanceResult,
            metadata: metadataResult.metadata
        };
    }
    async getSpecificTokenBalance(params) {
        // Handle empty tokenId (ERC-20 case)
        if (!params.tokenId) {
            return this.getTokenBalance({ ...params, tokenId: null });
        }
        // Get both balance and metadata for NFTs
        return this.getTokenWithBalance(params);
    }
    async getTokenCollection(params) {
        // Input validation
        if (!params.accountAddress || !params.contractAddress) {
            return this.emptyCollection(params, 'Invalid accountAddress or contractAddress');
        }
        try {
            const abi = convertAbiToInterface(params.abi);
            const analysis = this.contractService.analyzeContract(abi);
            // ERC-1155 balanceOfBatch can handle large batches efficiently (1 RPC call)
            // Default to 100 for ERC-1155, 20 for ERC-721 (which has no batch function)
            const defaultBatchSize = analysis.isERC1155 ? 100 : 20;
            const batchSize = Math.min(params.batchSize || defaultBatchSize, analysis.isERC1155 ? 200 : 50);
            // ERC-1155: Requires specific token IDs
            if (analysis.isERC1155) {
                if (!params.tokenIds?.length) {
                    return this.emptyCollection(params, 'ERC-1155 requires specific tokenIds parameter');
                }
                return this.processTokenBatch(params, params.tokenIds, batchSize, 'ERC-1155');
            }
            // ERC-721: Enumerate tokens directly (no caching)
            if (analysis.isERC721) {
                if (analysis.hasEnumeration) {
                    // Get current balance to know how many tokens to enumerate
                    const currentBalance = await this.web3Api.getTokenBalance({
                        ...params, abi, tokenId: null
                    });
                    if (currentBalance === 0) {
                        return this.emptyCollection(params, 'No tokens owned');
                    }
                    const maxTokens = Math.min(params.maxTokens || currentBalance, currentBalance, 100);
                    const tokenIds = await this.getTokenIdsByEnumeration(params, abi, maxTokens);
                    if (tokenIds.length === 0) {
                        return this.emptyCollection(params, 'No tokens owned');
                    }
                    return this.processTokenBatch(params, tokenIds, batchSize, 'ERC-721');
                }
                else if (params.tokenIds?.length) {
                    return this.processTokenBatch(params, params.tokenIds, batchSize, 'ERC-721');
                }
                else {
                    return this.emptyCollection(params, 'ERC-721 without enumeration requires tokenIds parameter');
                }
            }
            return this.emptyCollection(params, `Unsupported token standard (ERC721: ${analysis.isERC721}, ERC1155: ${analysis.isERC1155})`);
        }
        catch (error) {
            const message = error instanceof Error ? error.message : 'Unknown error';
            console.error('Collection retrieval error:', error);
            return this.emptyCollection(params, `Collection retrieval failed: ${message}`);
        }
    }
    /**
     * Enumerate token IDs owned by account (no caching - fresh data each call)
     */
    async getTokenIdsByEnumeration(params, abi, maxTokens) {
        const promises = Array.from({ length: maxTokens }, (_, i) => this.web3Api.getTokenOfOwnerByIndex({
            ...params, abi, tokenIndex: i
        }).catch(() => null) // Silently handle index out of bounds
        );
        const results = await Promise.all(promises);
        return results.filter((id) => id !== null);
    }
    async processTokenBatch(params, tokenIds, batchSize, tokenStandard) {
        const tokens = [];
        const errors = [];
        const abi = convertAbiToInterface(params.abi);
        // Process in batches
        for (let i = 0; i < tokenIds.length; i += batchSize) {
            const batch = tokenIds.slice(i, i + batchSize);
            let validTokens;
            if (tokenStandard === 'ERC-721') {
                // For ERC-721: Skip balance check, we know ownership from enumeration or explicit tokenIds
                validTokens = batch.map(tokenId => ({
                    tokenId,
                    balance: 1,
                    hasBalance: true,
                    metadata: null
                }));
            }
            else {
                // For ERC-1155: Use native balanceOfBatch (1 RPC call for entire batch)
                const batchResults = await this.web3Api.getBatchTokenBalances({
                    accountAddress: params.accountAddress,
                    contractAddress: params.contractAddress,
                    abi,
                    tokenIds: batch,
                    chainId: params.chainId,
                    isERC1155: true
                });
                // Convert batch results to TokenBalance format
                validTokens = batchResults
                    .filter(r => r.success && r.balance > 0n)
                    .map(r => ({
                    tokenId: r.tokenId,
                    balance: Number(r.balance),
                    hasBalance: true,
                    metadata: null
                }));
                // Track errors
                batchResults.filter(r => !r.success).forEach(r => {
                    errors.push(`${r.tokenId}: ${r.error}`);
                });
            }
            // Step 2: Get metadata for tokens that have balance
            if (validTokens.length > 0) {
                const metadataPromises = validTokens.map(token => this.getTokenMetadata({
                    contractAddress: params.contractAddress,
                    abi: params.abi,
                    tokenId: token.tokenId,
                    chainId: params.chainId
                })
                    .then(metadata => ({
                    tokenId: token.tokenId,
                    balance: token.balance,
                    hasBalance: token.hasBalance,
                    metadata: metadata.metadata
                }))
                    .catch(error => {
                    console.warn(`Metadata fetch failed for ${token.tokenId}:`, error.message);
                    return {
                        tokenId: token.tokenId,
                        balance: token.balance,
                        hasBalance: token.hasBalance,
                        metadata: null
                    };
                }));
                const finalTokens = await Promise.all(metadataPromises);
                tokens.push(...finalTokens);
            }
        }
        return {
            accountAddress: params.accountAddress,
            contractAddress: params.contractAddress,
            totalBalance: tokens.reduce((sum, token) => sum + token.balance, 0),
            tokensRetrieved: tokens.length,
            tokens,
            note: errors.length > 0 ? `${errors.length} tokens failed to load` : undefined
        };
    }
    emptyCollection(params, note) {
        return {
            accountAddress: params.accountAddress,
            contractAddress: params.contractAddress,
            totalBalance: 0,
            tokensRetrieved: 0,
            tokens: [],
            note
        };
    }
    clearCache() {
        this.cache.clear();
    }
}

/**
 * MetadataDomainService - Clean IPFS metadata resolution with centralized caching
 *
 * IPFS gateway is resolved from tenant configuration (chain-agnostic).
 */
class MetadataDomainService {
    constructor(ipfsApi) {
        this.ipfsApi = ipfsApi;
        this.cache = globalCacheService.createNamespace('metadata');
    }
    async fetchAndProcessMetadata(tokenUri) {
        const cacheKey = `fetch:${tokenUri}`;
        return this.cache.getOrSet(cacheKey, async () => {
            try {
                return await this.ipfsApi.fetchAndProcessMetadata(tokenUri);
            }
            catch (error) {
                console.error(`Error fetching metadata for ${tokenUri}:`, error);
                return null;
            }
        }, CacheTTL.METADATA);
    }
    async resolveIPFSUrl(url) {
        const cacheKey = `resolve:${url}`;
        return this.cache.getOrSet(cacheKey, () => this.ipfsApi.resolveIPFSUrl(url), CacheTTL.GATEWAY);
    }
    /**
     * Get IPFS gateway domain from tenant configuration.
     * Useful for pipes that need to resolve URLs synchronously after initial fetch.
     */
    async getIpfsGatewayDomain() {
        return this.ipfsApi.getIpfsGatewayDomain();
    }
    /**
     * Clear all cached metadata and URLs for this service
     */
    clearCache() {
        this.cache.clear();
    }
}

/**
 * ContractDomainService - Clean contract analysis without external dependencies
 */
class ContractDomainService {
    constructor() { }
    analyzeContract(abi) {
        const cleanAbi = convertAbiToInterface(abi);
        const methods = cleanAbi.fragments.filter(item => item.type === 'function').map(item => item.name);
        // ERC-721 detection
        const hasOwnerOf = methods.includes('ownerOf');
        const hasTokenURI = methods.includes('tokenURI');
        const hasTransferFrom = methods.includes('transferFrom');
        const isERC721 = hasOwnerOf && hasTokenURI && hasTransferFrom;
        // ERC-1155 detection
        const hasBalanceOfBatch = methods.includes('balanceOfBatch');
        const hasSafeBatchTransferFrom = methods.includes('safeBatchTransferFrom');
        const hasURI = methods.includes('uri');
        const isERC1155 = hasBalanceOfBatch && hasSafeBatchTransferFrom && hasURI;
        return {
            hasEnumeration: methods.includes('tokenByIndex') && methods.includes('totalSupply'),
            hasOwnerOf,
            hasBalanceOf: methods.includes('balanceOf'),
            hasTokenURI,
            hasTransfer: methods.includes('transfer') || methods.includes('transferFrom'),
            hasApprove: methods.includes('approve'),
            isERC721,
            isERC1155
        };
    }
}

/**
 * TokenCollectionManager - Auto-refresh token collections on wallet events
 *
 * This manager provides:
 * 1. Cached token collection storage with automatic refresh
 * 2. Auto-subscription to wallet events (transfers, mints, burns)
 * 3. Targeted invalidation based on contract address
 * 4. Subscriber pattern for reactive updates
 *
 * @example
 * ```typescript
 * // Get from SDK
 * const manager = sdk.tokens.collectionManager;
 *
 * // Subscribe to collection updates
 * const unsubscribe = manager.subscribe((collection, contractAddress) => {
 *   console.log(`Collection updated for ${contractAddress}`);
 *   updateUI(collection);
 * });
 *
 * // Fetch collection (auto-cached, auto-refreshed on wallet events)
 * const collection = await manager.getCollection({
 *   accountAddress: '0x...',
 *   contractAddress: '0x...',
 *   abi: contractAbi,
 *   chainId: 39123,
 *   tokenIds: ['1', '2', '3']
 * });
 *
 * // Cleanup
 * unsubscribe();
 * ```
 */
/**
 * Manages token collections with automatic refresh on wallet events
 */
class TokenCollectionManager {
    constructor(tokenService, eventEmitter, config) {
        this.tokenService = tokenService;
        this.eventEmitter = eventEmitter;
        this.collections = new Map();
        this.handlers = new Set();
        this.pendingRefresh = new Map();
        this.config = {
            autoRefreshOnWalletEvents: config?.autoRefreshOnWalletEvents ?? true,
            eventDebounceMs: config?.eventDebounceMs ?? 500,
            debug: config?.debug ?? false
        };
        if (this.config.autoRefreshOnWalletEvents && eventEmitter) {
            this.setupEventSubscription();
        }
    }
    /**
     * Subscribe to wallet events for auto-refresh
     */
    setupEventSubscription() {
        if (!this.eventEmitter) {
            return;
        }
        this.eventUnsubscribe = this.eventEmitter.subscribe((event) => {
            const contractAddress = event.details?.contractAddress;
            this.handleWalletEvent(contractAddress, event.type);
        }, { domains: ['wallet'] });
    }
    /**
     * Get a token collection (cached, auto-refreshed on wallet events)
     *
     * @param params - Collection parameters
     * @returns Promise resolving to the token collection
     */
    async getCollection(params) {
        const key = this.getCacheKey(params);
        const cached = this.collections.get(key);
        // Return cached if available and not stale
        if (cached?.lastCollection) {
            this.log(`Cache HIT for ${key}`);
            return cached.lastCollection;
        }
        this.log(`Cache MISS for ${key}, fetching...`);
        return this.fetchAndCache(params);
    }
    /**
     * Force refresh a specific collection
     *
     * @param contractAddress - Contract address to refresh
     * @param accountAddress - Account address to refresh
     * @returns Promise that resolves when refresh is complete
     */
    async refreshCollection(contractAddress, accountAddress) {
        const toRefresh = [];
        for (const [key, entry] of this.collections) {
            if (entry.params.contractAddress === contractAddress) {
                if (!accountAddress || entry.params.accountAddress === accountAddress) {
                    toRefresh.push(key);
                }
            }
        }
        await Promise.all(toRefresh.map(key => {
            const entry = this.collections.get(key);
            if (entry) {
                return this.refreshEntry(key, entry, 'refresh');
            }
        }));
    }
    /**
     * Subscribe to collection change events
     *
     * @param handler - Callback when any tracked collection changes
     * @returns Unsubscribe function
     */
    subscribe(handler) {
        this.handlers.add(handler);
        return () => this.handlers.delete(handler);
    }
    /**
     * Get all currently tracked collections
     */
    getTrackedCollections() {
        return Array.from(this.collections.entries()).map(([_, entry]) => ({
            contractAddress: entry.params.contractAddress,
            accountAddress: entry.params.accountAddress,
            collection: entry.lastCollection
        }));
    }
    /**
     * Check if a collection is currently being tracked
     */
    isTracking(contractAddress, accountAddress) {
        const key = `${contractAddress}:${accountAddress}`;
        return this.collections.has(key);
    }
    /**
     * Clear tracking for a specific collection
     */
    untrack(contractAddress, accountAddress) {
        if (accountAddress) {
            const key = `${contractAddress}:${accountAddress}`;
            this.collections.delete(key);
            this.log(`Untracked ${key}`);
        }
        else {
            // Untrack all for this contract
            for (const key of this.collections.keys()) {
                if (key.startsWith(`${contractAddress}:`)) {
                    this.collections.delete(key);
                }
            }
            this.log(`Untracked all collections for ${contractAddress}`);
        }
    }
    /**
     * Clear all tracked collections
     */
    clearAll() {
        this.collections.clear();
        this.log('Cleared all tracked collections');
    }
    /**
     * Handle wallet events (with debouncing)
     */
    handleWalletEvent(contractAddress, eventType) {
        // Determine which collections to refresh
        const keysToRefresh = [];
        for (const [key, entry] of this.collections) {
            // If no specific contract, refresh all; otherwise match contract
            if (!contractAddress || entry.params.contractAddress.toLowerCase() === contractAddress.toLowerCase()) {
                keysToRefresh.push(key);
            }
        }
        if (keysToRefresh.length === 0) {
            this.log(`No tracked collections affected by event for ${contractAddress || 'all contracts'}`);
            return;
        }
        // Debounce refresh per key
        for (const key of keysToRefresh) {
            const existing = this.pendingRefresh.get(key);
            if (existing) {
                clearTimeout(existing);
            }
            const timeout = setTimeout(async () => {
                this.pendingRefresh.delete(key);
                const entry = this.collections.get(key);
                if (entry) {
                    this.log(`Refreshing ${key} due to wallet event: ${eventType}`);
                    await this.refreshEntry(key, entry, eventType);
                }
            }, this.config.eventDebounceMs);
            this.pendingRefresh.set(key, timeout);
        }
    }
    /**
     * Fetch and cache a collection
     */
    async fetchAndCache(params) {
        const key = this.getCacheKey(params);
        // Mark as refreshing to prevent duplicate fetches
        const existing = this.collections.get(key);
        if (existing?.refreshing) {
            // Wait for existing refresh to complete
            return existing.lastCollection || this.emptyCollection(params);
        }
        this.collections.set(key, {
            params,
            lastCollection: null,
            timestamp: Date.now(),
            refreshing: true
        });
        try {
            const collection = await this.tokenService.getTokenCollection(params);
            this.collections.set(key, {
                params,
                lastCollection: collection,
                timestamp: Date.now(),
                refreshing: false
            });
            this.log(`Cached collection for ${key}: ${collection.tokensRetrieved} tokens`);
            return collection;
        }
        catch (error) {
            // Remove failed entry
            this.collections.delete(key);
            throw error;
        }
    }
    /**
     * Refresh a cached entry and notify handlers
     */
    async refreshEntry(key, entry, source) {
        if (entry.refreshing) {
            this.log(`Skipping refresh for ${key} - already refreshing`);
            return;
        }
        entry.refreshing = true;
        try {
            const newCollection = await this.tokenService.getTokenCollection(entry.params);
            entry.lastCollection = newCollection;
            entry.timestamp = Date.now();
            entry.refreshing = false;
            this.log(`Refreshed ${key}: ${newCollection.tokensRetrieved} tokens`);
            // Notify subscribers
            for (const handler of this.handlers) {
                try {
                    handler(newCollection, entry.params.contractAddress, {
                        type: source === 'refresh' ? 'refresh' : 'wallet_event',
                        source
                    });
                }
                catch (handlerError) {
                    console.error('[TokenCollectionManager] Handler error:', handlerError);
                }
            }
        }
        catch (error) {
            entry.refreshing = false;
            console.error(`[TokenCollectionManager] Refresh failed for ${key}:`, error);
        }
    }
    /**
     * Generate cache key from params
     */
    getCacheKey(params) {
        return `${params.contractAddress}:${params.accountAddress}`;
    }
    /**
     * Create empty collection placeholder
     */
    emptyCollection(params) {
        return {
            accountAddress: params.accountAddress,
            contractAddress: params.contractAddress,
            totalBalance: 0,
            tokensRetrieved: 0,
            tokens: [],
            note: 'Loading...'
        };
    }
    /**
     * Debug logging
     */
    log(message, data) {
        if (this.config.debug) {
            console.log(`[TokenCollectionManager] ${message}`, data || '');
        }
    }
    /**
     * Cleanup resources
     */
    destroy() {
        // Clear pending refreshes
        for (const timeout of this.pendingRefresh.values()) {
            clearTimeout(timeout);
        }
        this.pendingRefresh.clear();
        // Unsubscribe from events
        this.eventUnsubscribe?.();
        // Clear handlers and collections
        this.handlers.clear();
        this.collections.clear();
        this.log('Destroyed');
    }
}

/**
 * BalanceManager - Auto-refresh ERC-20 token balances on wallet events
 *
 * This manager provides:
 * 1. Cached balance storage with automatic refresh
 * 2. Auto-subscription to wallet events (transfers)
 * 3. Targeted invalidation based on contract address
 * 4. Subscriber pattern for reactive updates
 *
 * @example
 * ```typescript
 * // Get from SDK
 * const manager = sdk.tokens.balanceManager;
 *
 * // Subscribe to balance updates
 * const unsubscribe = manager.subscribe((balance, contractAddress) => {
 *   console.log(`Balance updated for ${contractAddress}: ${balance}`);
 *   updateUI(balance);
 * });
 *
 * // Fetch balance (auto-cached, auto-refreshed on wallet events)
 * const balance = await manager.getBalance({
 *   accountAddress: '0x...',
 *   contractAddress: '0x...',
 *   abi: contractAbi,
 *   chainId: 39123
 * });
 *
 * // Manual refresh
 * await manager.refreshBalance(contractAddress);
 *
 * // Cleanup
 * unsubscribe();
 * ```
 */
/**
 * Manages ERC-20 token balances with automatic refresh on wallet events
 */
class BalanceManager {
    constructor(tokenService, eventEmitter, config) {
        this.tokenService = tokenService;
        this.eventEmitter = eventEmitter;
        this.balances = new Map();
        this.handlers = new Set();
        this.pendingRefresh = new Map();
        this.config = {
            autoRefreshOnWalletEvents: config?.autoRefreshOnWalletEvents ?? true,
            eventDebounceMs: config?.eventDebounceMs ?? 500,
            debug: config?.debug ?? false
        };
        if (this.config.autoRefreshOnWalletEvents && eventEmitter) {
            this.setupEventSubscription();
        }
    }
    /**
     * Subscribe to wallet events for auto-refresh
     */
    setupEventSubscription() {
        if (!this.eventEmitter) {
            return;
        }
        this.eventUnsubscribe = this.eventEmitter.subscribe((event) => {
            const contractAddress = event.details?.contractAddress;
            this.handleWalletEvent(contractAddress, event.type);
        }, { domains: ['wallet'] });
    }
    /**
     * Get a token balance (cached, auto-refreshed on wallet events)
     *
     * @param params - Balance parameters
     * @returns Promise resolving to the token balance
     */
    async getBalance(params) {
        const key = this.getCacheKey(params);
        const cached = this.balances.get(key);
        // Return cached if available and not stale
        if (cached?.lastBalance) {
            this.log(`Cache HIT for ${key}`);
            return cached.lastBalance;
        }
        this.log(`Cache MISS for ${key}, fetching...`);
        return this.fetchAndCache(params);
    }
    /**
     * Force refresh a specific balance
     *
     * @param contractAddress - Contract address to refresh
     * @param accountAddress - Account address to refresh (optional, refreshes all if not provided)
     * @returns Promise that resolves when refresh is complete
     */
    async refreshBalance(contractAddress, accountAddress) {
        const toRefresh = [];
        for (const [key, entry] of this.balances) {
            if (entry.params.contractAddress.toLowerCase() === contractAddress.toLowerCase()) {
                if (!accountAddress || entry.params.accountAddress.toLowerCase() === accountAddress.toLowerCase()) {
                    toRefresh.push(key);
                }
            }
        }
        await Promise.all(toRefresh.map(key => {
            const entry = this.balances.get(key);
            if (entry) {
                return this.refreshEntry(key, entry, 'refresh');
            }
        }));
    }
    /**
     * Force refresh all tracked balances
     *
     * @returns Promise that resolves when all refreshes are complete
     */
    async refreshAll() {
        const refreshPromises = Array.from(this.balances.entries()).map(([key, entry]) => this.refreshEntry(key, entry, 'refresh'));
        await Promise.all(refreshPromises);
    }
    /**
     * Subscribe to balance change events
     *
     * @param handler - Callback when any tracked balance changes
     * @returns Unsubscribe function
     */
    subscribe(handler) {
        this.handlers.add(handler);
        return () => this.handlers.delete(handler);
    }
    /**
     * Get all currently tracked balances
     */
    getTrackedBalances() {
        return Array.from(this.balances.entries()).map(([_, entry]) => ({
            contractAddress: entry.params.contractAddress,
            accountAddress: entry.params.accountAddress,
            balance: entry.lastBalance
        }));
    }
    /**
     * Check if a balance is currently being tracked
     */
    isTracking(contractAddress, accountAddress) {
        const key = `${contractAddress.toLowerCase()}:${accountAddress.toLowerCase()}`;
        return this.balances.has(key);
    }
    /**
     * Clear tracking for a specific balance
     */
    untrack(contractAddress, accountAddress) {
        if (accountAddress) {
            const key = `${contractAddress.toLowerCase()}:${accountAddress.toLowerCase()}`;
            this.balances.delete(key);
            this.log(`Untracked ${key}`);
        }
        else {
            // Untrack all for this contract
            const toDelete = [];
            for (const key of this.balances.keys()) {
                if (key.startsWith(`${contractAddress.toLowerCase()}:`)) {
                    toDelete.push(key);
                }
            }
            toDelete.forEach(key => this.balances.delete(key));
            this.log(`Untracked all balances for ${contractAddress}`);
        }
    }
    /**
     * Clear all tracked balances
     */
    clearAll() {
        this.balances.clear();
        this.log('Cleared all tracked balances');
    }
    /**
     * Handle wallet events (with debouncing)
     */
    handleWalletEvent(contractAddress, eventType) {
        // Determine which balances to refresh
        const keysToRefresh = [];
        for (const [key, entry] of this.balances) {
            // If no specific contract, refresh all; otherwise match contract
            if (!contractAddress || entry.params.contractAddress.toLowerCase() === contractAddress.toLowerCase()) {
                keysToRefresh.push(key);
            }
        }
        if (keysToRefresh.length === 0) {
            this.log(`No tracked balances affected by event for ${contractAddress || 'all contracts'}`);
            return;
        }
        // Debounce refresh per key
        for (const key of keysToRefresh) {
            const existing = this.pendingRefresh.get(key);
            if (existing) {
                clearTimeout(existing);
            }
            const timeout = setTimeout(async () => {
                this.pendingRefresh.delete(key);
                const entry = this.balances.get(key);
                if (entry) {
                    this.log(`Refreshing ${key} due to wallet event: ${eventType}`);
                    await this.refreshEntry(key, entry, eventType);
                }
            }, this.config.eventDebounceMs);
            this.pendingRefresh.set(key, timeout);
        }
    }
    /**
     * Fetch and cache a balance
     */
    async fetchAndCache(params) {
        const key = this.getCacheKey(params);
        // Mark as refreshing to prevent duplicate fetches
        const existing = this.balances.get(key);
        if (existing?.refreshing) {
            // Wait for existing refresh to complete
            return existing.lastBalance || this.emptyBalance();
        }
        this.balances.set(key, {
            params,
            lastBalance: null,
            timestamp: Date.now(),
            refreshing: true
        });
        try {
            const balance = await this.tokenService.getTokenBalance({
                ...params,
                tokenId: null // ERC-20 has no tokenId
            });
            this.balances.set(key, {
                params,
                lastBalance: balance,
                timestamp: Date.now(),
                refreshing: false
            });
            this.log(`Cached balance for ${key}: ${balance.balance}`);
            return balance;
        }
        catch (error) {
            // Remove failed entry
            this.balances.delete(key);
            throw error;
        }
    }
    /**
     * Refresh a cached entry and notify handlers
     */
    async refreshEntry(key, entry, source) {
        if (entry.refreshing) {
            this.log(`Skipping refresh for ${key} - already refreshing`);
            return;
        }
        entry.refreshing = true;
        try {
            const newBalance = await this.tokenService.getTokenBalance({
                ...entry.params,
                tokenId: null // ERC-20 has no tokenId
            });
            entry.lastBalance = newBalance;
            entry.timestamp = Date.now();
            entry.refreshing = false;
            this.log(`Refreshed ${key}: ${newBalance.balance}`);
            // Notify subscribers
            for (const handler of this.handlers) {
                try {
                    handler(newBalance, entry.params.contractAddress, {
                        type: source === 'refresh' ? 'refresh' : 'wallet_event',
                        source
                    });
                }
                catch (handlerError) {
                    console.error('[BalanceManager] Handler error:', handlerError);
                }
            }
        }
        catch (error) {
            entry.refreshing = false;
            console.error(`[BalanceManager] Refresh failed for ${key}:`, error);
        }
    }
    /**
     * Generate cache key from params
     */
    getCacheKey(params) {
        return `${params.contractAddress.toLowerCase()}:${params.accountAddress.toLowerCase()}`;
    }
    /**
     * Create empty balance placeholder
     */
    emptyBalance() {
        return {
            tokenId: null,
            balance: 0,
            hasBalance: false,
            metadata: null
        };
    }
    /**
     * Debug logging
     */
    log(message, data) {
        if (this.config.debug) {
            console.log(`[BalanceManager] ${message}`, data || '');
        }
    }
    /**
     * Cleanup resources
     */
    destroy() {
        // Clear pending refreshes
        for (const timeout of this.pendingRefresh.values()) {
            clearTimeout(timeout);
        }
        this.pendingRefresh.clear();
        // Unsubscribe from events
        this.eventUnsubscribe?.();
        // Clear handlers and balances
        this.handlers.clear();
        this.balances.clear();
        this.log('Destroyed');
    }
}

/**
 * Web3ApplicationService - Application layer entrance point
 * Orchestrates domain services and provides clean public interface
 * Simplified architecture with concrete classes
 */
class Web3ApplicationService {
    constructor(web3Api, ipfsApi) {
        // Create domain services with injected infrastructure dependencies
        this.contractDomainService = new ContractDomainService();
        this.metadataDomainService = new MetadataDomainService(ipfsApi);
        this.tokenDomainService = new TokenDomainService(web3Api, this.metadataDomainService, this.contractDomainService);
    }
    /**
     * Get balance and metadata for a specific token
     */
    async getSpecificTokenBalance(request) {
        return this.tokenDomainService.getSpecificTokenBalance({
            accountAddress: request.accountAddress || '',
            contractAddress: request.contractAddress,
            abi: request.abi,
            tokenId: request.tokenId || '',
            chainId: request.chainId
        });
    }
    /**
     * Get metadata for a specific token from on-chain.
     *
     * Returns metadata with:
     * - Normalized property names (imageUrl, animationUrl - SDK standard)
     * - All IPFS URLs already resolved to HTTPS
     * - Custom properties preserved from original metadata
     *
     * Note: Normalization happens at infrastructure layer (IPFS API),
     * so no additional transformation needed here.
     */
    async getTokenMetadata(request) {
        const domainResult = await this.tokenDomainService.getTokenMetadata({
            contractAddress: request.contractAddress,
            abi: request.abi,
            tokenId: request.tokenId || '',
            chainId: request.chainId
        });
        // Metadata is already normalized at infrastructure layer
        return domainResult.metadata;
    }
    /**
     * Retrieve entire collection of tokens with balance and metadata
     */
    async getTokenCollection(request) {
        return this.tokenDomainService.getTokenCollection({
            accountAddress: request.accountAddress || '',
            contractAddress: request.contractAddress,
            abi: request.abi,
            chainId: request.chainId,
            maxTokens: request.maxTokens,
            tokenIds: request.tokenIds
        });
    }
    /**
     * Resolve IPFS URLs to HTTPS if needed.
     *
     * IPFS gateway is resolved from tenant configuration (chain-agnostic).
     */
    async resolveIPFSUrl(url) {
        return this.metadataDomainService.resolveIPFSUrl(url);
    }
    /**
     * Get IPFS gateway domain from tenant configuration.
     * Useful for pipes that need to resolve URLs synchronously after initial fetch.
     *
     * @returns Gateway domain (e.g., 'pers.mypinata.cloud')
     */
    async getIpfsGatewayDomain() {
        return this.metadataDomainService.getIpfsGatewayDomain();
    }
    /**
     * Fetch and process metadata from any URI with IPFS conversion.
     *
     * Use this for ad-hoc metadata fetching when you have a tokenURI.
     * Normalization happens at infrastructure layer.
     * IPFS gateway is resolved from tenant configuration (chain-agnostic).
     */
    async fetchAndProcessMetadata(tokenUri) {
        return this.metadataDomainService.fetchAndProcessMetadata(tokenUri);
    }
}

/**
 * Web3InfrastructureApi - Infrastructure implementation for blockchain operations
 * Uses @explorins/web3-ts for Web3 interactions
 */
class Web3InfrastructureApi {
    constructor(web3ChainService) {
        this.web3ChainService = web3ChainService;
    }
    async getTokenBalance(request) {
        try {
            if (request.tokenId !== null)
                request.tokenId = request.tokenId.toString();
            const ethersProvider = await this.web3ChainService.getEthersProviderByChainId(request.chainId);
            const contract = createSafeContract(request.contractAddress, request.abi, ethersProvider);
            return await getAccountTokenBalance(contract, request.accountAddress, request.tokenId);
        }
        catch (error) {
            console.error(`Failed to get token balance for ${request.accountAddress} for ${request.contractAddress} and tokenId ${request.tokenId}, return 0 instead:`, error);
            return 0;
        }
    }
    /**
     * Get multiple ERC-1155 token balances in a single RPC call using native balanceOfBatch.
     * Falls back to individual calls if balanceOfBatch fails.
     *
     * @param request - Batch balance request
     * @returns Array of balance results per tokenId
     */
    async getBatchTokenBalances(request) {
        try {
            const ethersProvider = await this.web3ChainService.getEthersProviderByChainId(request.chainId);
            const results = await getBatchBalancesWithFallback(ethersProvider, request.contractAddress, request.abi, request.accountAddress, request.tokenIds, request.isERC1155 ?? true);
            return results;
        }
        catch (error) {
            console.error(`Failed to get batch token balances:`, error);
            // Return empty results on failure
            return request.tokenIds.map(tokenId => ({
                tokenId,
                balance: 0n,
                success: false,
                error: `${error}`
            }));
        }
    }
    async getTokenUri(request) {
        try {
            const ethersProvider = await this.web3ChainService.getEthersProviderByChainId(request.chainId);
            const contract = createSafeContract(request.contractAddress, request.abi, ethersProvider);
            const tokenId = Number(request.tokenId);
            const tokenUri = await getTokenUri(contract, tokenId);
            return String(tokenUri);
        }
        catch (error) {
            console.error(`Failed to get token URI for tokenId ${request.tokenId}:`, error);
            throw error;
        }
    }
    async getTokenOfOwnerByIndex(request) {
        try {
            const ethersProvider = await this.web3ChainService.getEthersProviderByChainId(request.chainId);
            const tokenContract = createSafeContract(request.contractAddress, request.abi, ethersProvider);
            const tokenId = await getTokenOfOwnerByIndex(tokenContract, request.accountAddress, request.tokenIndex);
            return String(tokenId);
        }
        catch (error) {
            console.error(`Failed to get token by index ${request.tokenIndex} for ${request.accountAddress}:`, error);
            throw error;
        }
    }
}

/**
 * IPFSInfrastructureApi - Infrastructure implementation for IPFS operations
 * Uses TenantManager for IPFS gateway resolution with centralized caching
 *
 * IMPORTANT: IPFS gateway domain is now fetched from tenant configuration (TenantClientConfigDTO),
 * not from chain data. This is the correct architecture since IPFS is chain-agnostic.
 */
class IPFSInfrastructureApi {
    constructor(tenantManager) {
        this.tenantManager = tenantManager;
        this.cache = globalCacheService.createNamespace('ipfs');
    }
    /**
     * Get IPFS gateway domain from tenant configuration.
     *
     * @returns The IPFS gateway domain from tenant configuration
     * @throws Error if tenant config is not found or ipfsGatewayDomain is not configured
     */
    async getIpfsGatewayDomain() {
        const cacheKey = 'gateway';
        return this.cache.getOrSet(cacheKey, async () => {
            const clientConfig = await this.tenantManager.getClientConfig();
            if (!clientConfig.ipfsGatewayDomain) {
                throw new Error(`IPFS gateway domain not configured for tenant. ` +
                    `Please configure ipfsGatewayDomain in the tenant configuration.`);
            }
            return clientConfig.ipfsGatewayDomain;
        }, CacheTTL.GATEWAY);
    }
    /**
     * Resolve IPFS URL to HTTPS URL using tenant's configured gateway.
     *
     * @param url - The URL to resolve (can be ipfs:// or https://)
     * @returns Resolved HTTPS URL
     */
    async resolveIPFSUrl(url) {
        if (url.startsWith('ipfs://')) {
            const gateway = await this.getIpfsGatewayDomain();
            return url.replace('ipfs://', `https://${gateway}/ipfs/`);
        }
        return url;
    }
    /**
     * Fetch metadata from tokenURI, resolve all IPFS URLs, and normalize to SDK format.
     *
     * NORMALIZATION BOUNDARY: This is the single place where ERC metadata standard
     * (snake_case: image, animation_url) is converted to SDK format (camelCase: imageUrl, animationUrl).
     * All downstream consumers receive normalized SDK format with resolved HTTPS URLs.
     *
     * @param tokenUri - Token URI (can be ipfs:// or https://)
     * @returns Normalized TokenMetadata with resolved HTTPS URLs, or null on error
     */
    async fetchAndProcessMetadata(tokenUri) {
        try {
            const resolvedUri = await this.resolveIPFSUrl(tokenUri);
            const response = await fetch(resolvedUri);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const rawMetadata = await response.json();
            // Resolve any IPFS URLs in string fields
            for (const key of Object.keys(rawMetadata)) {
                const value = rawMetadata[key];
                if (typeof value === 'string' && value.startsWith('ipfs://')) {
                    rawMetadata[key] = await this.resolveIPFSUrl(value);
                }
            }
            // Extract custom properties (anything not in ERC standard)
            const customProperties = Object.fromEntries(Object.entries(rawMetadata).filter(([key]) => !['name', 'description', 'image', 'animation_url', 'external_url', 'attributes'].includes(key)));
            // Return normalized SDK format
            const result = {
                name: String(rawMetadata.name ?? ''),
                description: String(rawMetadata.description ?? ''),
                imageUrl: String(rawMetadata.image ?? ''),
                animationUrl: rawMetadata.animation_url ? String(rawMetadata.animation_url) : undefined,
                externalUrl: rawMetadata.external_url ? String(rawMetadata.external_url) : undefined,
                attributes: Array.isArray(rawMetadata.attributes) ? rawMetadata.attributes : [],
                ...customProperties
            };
            return result;
        }
        catch (error) {
            console.error('Error fetching metadata:', error);
            return null;
        }
    }
    async fetchFromUrl(url) {
        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`Failed to fetch from ${url}: ${response.statusText}`);
            }
            return await response.json();
        }
        catch (error) {
            console.error(`Error fetching from URL ${url}:`, error);
            throw error;
        }
    }
    /**
     * Clear IPFS cache for this service
     */
    clearCache() {
        this.cache.clear();
    }
}

/**
 * Generate explorer URL for a given address or transaction
 *
 * @param chainData - Chain data containing explorer URL and chain ID
 * @param address - Address or transaction hash
 * @param type - Type of explorer URL ('address' or 'tx')
 * @returns Explorer URL string
 */
function getExplorerUrl(chainData, address, type) {
    if (chainData?.explorerUrl) {
        return `${chainData.explorerUrl}/${type}/${address}`;
    }
    // Fallback to hardcoded URLs if no explorer URL in chain data
    const chainId = chainData?.chainId;
    let url = '';
    switch (chainId) {
        case 1:
            url = `https://etherscan.io/${type}/${address}`;
            break;
        case 4:
            url = `https://rinkeby.etherscan.io/${type}/${address}`;
            break;
        case 56:
            url = `https://bscscan.com/${type}/${address}`;
            break;
        case 97:
            url = `https://testnet.bscscan.com/${type}/${address}`;
            break;
        case 137:
            url = `https://explorer-mainnet.maticvigil.com/${type}/${address}`;
            break;
        case 80001:
            url = `https://explorer-mumbai.maticvigil.com/${type}/${address}`;
            break;
        case 80002:
            url = `https://www.oklink.com/amoy/${type}/${address}`;
            break;
        default:
            url = `https://etherscan.io/${type}/${address}`;
    }
    return url;
}
/**
 * Generate explorer URL using chain ID (async version that fetches chain data)
 *
 * @param getChainData - Function to get chain data by ID
 * @param chainId - Chain ID to get explorer URL for
 * @param address - Address or transaction hash
 * @param type - Type of explorer URL ('address' or 'tx')
 * @returns Promise resolving to explorer URL string
 */
async function getExplorerUrlByChainId(getChainData, chainId, address, type) {
    const chainData = await getChainData(chainId);
    return getExplorerUrl(chainData, address, type);
}

/**
 * Web3 Manager - Clean, high-level interface for Web3 operations
 *
 * Provides a simplified API for common Web3 blockchain tasks while maintaining
 * access to the full Web3 SDK for advanced use cases.
 *
 * ## Supported Token Standards
 *
 * - **ERC-20**: Fungible tokens (credits, points). Query balance with `getTokenBalance()`.
 * - **ERC-721**: Non-fungible tokens (unique NFTs). Supports enumeration - no tokenIds needed.
 * - **ERC-1155**: Multi-token standard (rewards, collectibles). Requires specific tokenIds.
 *
 * ## Recommended: Use Helper Methods
 *
 * For most use cases, use `getAccountOwnedTokensFromContract()` which automatically
 * handles token type detection and tokenId extraction:
 *
 * ```typescript
 * const result = await sdk.web3.getAccountOwnedTokensFromContract(walletAddress, token);
 * console.log(`Wallet owns ${result.totalOwned} tokens`);
 * ```
 *
 * ## Manual Approach (Advanced)
 *
 * For ERC-1155 tokens, tokenIds are extracted from `token.metadata[].tokenMetadataIncrementalId`:
 *
 * ```typescript
 * const tokenIds = token.metadata?.map(meta => meta.tokenMetadataIncrementalId.toString());
 * const collection = await sdk.web3.getTokenCollection({
 *   accountAddress: walletAddress,
 *   contractAddress: token.contractAddress,
 *   abi: token.abi,
 *   chainId: token.chainId,
 *   tokenIds  // Required for ERC-1155, optional for ERC-721
 * });
 * ```
 *
 * ## IPFS Resolution
 *
 * **Note:** IPFS is chain-agnostic. Use `sdk.tenants` for IPFS operations:
 *
 * ```typescript
 * const httpsUrl = await sdk.tenants.resolveIpfsUrl('ipfs://QmXxx.../image.png');
 * const metadata = await sdk.tenants.fetchAndProcessMetadata('ipfs://QmXxx.../metadata.json');
 * ```
 */
class Web3Manager {
    constructor(apiClient, tenantManager, eventEmitter) {
        this.apiClient = apiClient;
        // Use provided TenantManager or create one
        this.tenantManager = tenantManager || new TenantManager(apiClient);
        this.eventEmitter = eventEmitter;
        // Initialize Web3 Chain service
        const web3ChainApi = new Web3ChainApi(apiClient);
        this.web3ChainService = new Web3ChainService(web3ChainApi);
        // Initialize Web3 Application service with TenantManager for IPFS
        const web3InfrastructureApi = new Web3InfrastructureApi(this.web3ChainService);
        const ipfsInfrastructureApi = new IPFSInfrastructureApi(this.tenantManager);
        this.web3ApplicationService = new Web3ApplicationService(web3InfrastructureApi, ipfsInfrastructureApi);
        // Initialize domain services for managers
        const contractDomainService = new ContractDomainService();
        const metadataDomainService = new MetadataDomainService(ipfsInfrastructureApi);
        this.tokenDomainService = new TokenDomainService(web3InfrastructureApi, metadataDomainService, contractDomainService);
    }
    /**
     * Get the TokenCollectionManager for NFT collection operations.
     *
     * Features:
     * - Auto-refresh on wallet events (transfers, mints, burns)
     * - Cached collections
     * - Subscriber pattern for reactive updates
     *
     * @example
     * ```typescript
     * const manager = sdk.web3.collectionManager;
     *
     * // Subscribe to updates
     * const unsubscribe = manager.subscribe((collection, contractAddress) => {
     *   console.log(`Collection updated for ${contractAddress}`);
     * });
     *
     * // Fetch collection (auto-cached, auto-refreshed)
     * const collection = await manager.getCollection({...});
     *
     * // Manual refresh
     * await manager.refreshCollection(contractAddress);
     * ```
     */
    get collectionManager() {
        if (!this._collectionManager) {
            this._collectionManager = new TokenCollectionManager(this.tokenDomainService, this.eventEmitter);
        }
        return this._collectionManager;
    }
    /**
     * Get the BalanceManager for ERC-20 (credit token) balance operations.
     *
     * Features:
     * - Auto-refresh on wallet events (transfers)
     * - Cached balances
     * - Subscriber pattern for reactive updates
     *
     * @example
     * ```typescript
     * const manager = sdk.web3.balanceManager;
     *
     * // Subscribe to balance updates
     * const unsubscribe = manager.subscribe((balance, contractAddress) => {
     *   console.log(`Balance updated: ${balance.balance}`);
     * });
     *
     * // Fetch balance (auto-cached, auto-refreshed)
     * const balance = await manager.getBalance({
     *   accountAddress: '0x...',
     *   contractAddress: '0x...',
     *   abi: creditTokenAbi,
     *   chainId: 39123
     * });
     *
     * // Manual refresh
     * await manager.refreshBalance(contractAddress);
     * ```
     */
    get balanceManager() {
        if (!this._balanceManager) {
            this._balanceManager = new BalanceManager(this.tokenDomainService, this.eventEmitter);
        }
        return this._balanceManager;
    }
    /**
     * Get token balance for a specific token
     *
     * @param request - Token balance request parameters
     * @returns Promise resolving to token balance
     */
    async getTokenBalance(request) {
        return this.web3ApplicationService.getSpecificTokenBalance(request);
    }
    /**
     * Get token metadata
     *
     * @param request - Token metadata request parameters
     * @returns Promise resolving to token metadata or null if not found
     */
    async getTokenMetadata(request) {
        return this.web3ApplicationService.getTokenMetadata(request);
    }
    /**
     * Get token collection
     *
     * @param request - Token collection request parameters
     * @returns Promise resolving to token collection
     */
    async getTokenCollection(request) {
        return this.web3ApplicationService.getTokenCollection(request);
    }
    /**
     * Fetch and process token metadata
     *
     * @param tokenUri - Token URI to fetch metadata from
     * @returns Promise resolving to processed metadata or null if not found
     * @deprecated Use sdk.tenants.fetchAndProcessMetadata() instead - IPFS is chain-agnostic
     */
    async fetchAndProcessMetadata(tokenUri) {
        return this.tenantManager.fetchAndProcessMetadata(tokenUri);
    }
    /**
     * Get blockchain chain data by chain ID
     *
     * @param chainId - Chain ID to get data for
     * @returns Promise resolving to chain data or null if not found
     */
    async getChainDataById(chainId) {
        return this.web3ChainService.getChainDataWithCache(chainId);
    }
    /**
     * Get explorer URL for a given address or transaction
     *
     * @param chainId - Chain ID to get explorer URL for
     * @param address - Address or transaction hash
     * @param type - Type of explorer URL ('address' or 'tx')
     * @returns Promise resolving to explorer URL
     */
    async getExplorerUrl(chainId, address, type) {
        return getExplorerUrlByChainId((id) => this.getChainDataById(id), chainId, address, type);
    }
    /**
     * Get the full Web3 SDK for advanced operations
     *
     * @returns Web3SDK instance
     */
    getWeb3ApplicationService() {
        return this.web3ApplicationService;
    }
    // ==========================================
    // HELPER METHODS
    // ==========================================
    /**
     * Extract tokenIds from a TokenDTO's metadata.
     *
     * Extracts `tokenMetadataIncrementalId` from each metadata entry. This is
     * particularly useful for ERC-1155 tokens which require specific tokenIds
     * to query balances, but works with any token that has metadata.
     *
     * **Note:** For most use cases, prefer `getAccountOwnedTokensFromContract()`
     * which handles tokenId extraction automatically.
     *
     * @param token - Token definition containing metadata array
     * @returns Array of tokenId strings, or undefined if no metadata
     *
     * @example Manual token collection request
     * ```typescript
     * const rewardToken = (await sdk.tokens.getRewardTokens())[0];
     * const tokenIds = sdk.web3.extractTokenIds(rewardToken);
     *
     * if (tokenIds) {
     *   const collection = await sdk.web3.getTokenCollection({
     *     accountAddress: walletAddress,
     *     contractAddress: rewardToken.contractAddress,
     *     abi: rewardToken.abi,
     *     chainId: rewardToken.chainId,
     *     tokenIds
     *   });
     * }
     * ```
     *
     * @see {@link getAccountOwnedTokensFromContract} - Recommended helper that handles this automatically
     */
    extractTokenIds(token) {
        if (!token.metadata || token.metadata.length === 0) {
            return undefined;
        }
        return token.metadata.map(meta => meta.tokenMetadataIncrementalId.toString());
    }
    /**
     * Get owned tokens from a specific token contract for any blockchain address.
     *
     * **Recommended method** for querying token balances. Automatically handles:
     * - Token type detection (ERC-20, ERC-721, ERC-1155)
     * - TokenId extraction from metadata (uses `extractTokenIds()` internally for ERC-1155)
     * - Building the collection request
     * - Filtering to only tokens with balance > 0
     *
     * Works with any valid blockchain address - can query user wallets, external
     * wallets, contract addresses, or any other address holding tokens.
     *
     * @param accountAddress - Any valid blockchain address (wallet, contract, etc.)
     * @param token - Token definition (from getRewardTokens, getStatusTokens, etc.)
     * @param maxTokens - Maximum tokens to retrieve (default: 100, max: 100)
     * @returns Promise resolving to collection result with owned tokens
     *
     * @example Query user's wallet
     * ```typescript
     * const userWallet = user.wallets[0].address;
     * const result = await sdk.web3.getAccountOwnedTokensFromContract(userWallet, rewardToken);
     * console.log(`User owns ${result.totalOwned} tokens`);
     * ```
     *
     * @example Query any external wallet
     * ```typescript
     * const externalWallet = '0x1234...abcd';
     * const result = await sdk.web3.getAccountOwnedTokensFromContract(externalWallet, rewardToken);
     * console.log(`Wallet owns ${result.totalOwned} tokens`);
     * ```
     *
     * @see {@link extractTokenIds} - Low-level helper used internally for ERC-1155
     * @see {@link buildCollectionRequest} - For manual request building
     */
    async getAccountOwnedTokensFromContract(accountAddress, token, maxTokens = 100) {
        // For ERC-1155, extract tokenIds from metadata
        const tokenIds = token.type === NativeTokenTypes.ERC1155 ? this.extractTokenIds(token) : undefined;
        const collection = await this.getTokenCollection({
            accountAddress: accountAddress,
            contractAddress: token.contractAddress,
            abi: token.abi,
            chainId: token.chainId,
            tokenIds,
            maxTokens
        });
        // Filter to only owned tokens (hasBalance: true)
        // For ERC721, include all since they're enumerated
        const ownedTokens = token.type === NativeTokenTypes.ERC721
            ? collection.tokens
            : collection.tokens.filter(t => t.hasBalance);
        // Calculate total owned:
        // - ERC-1155: sum balances (can own multiple copies of same tokenId)
        // - ERC-721: count unique tokens (each token is unique, balance always 1)
        const totalOwned = token.type === NativeTokenTypes.ERC1155
            ? ownedTokens.reduce((sum, t) => sum + (t.balance || 0), 0)
            : ownedTokens.length;
        return {
            token,
            ownedTokens,
            totalOwned
        };
    }
    /**
     * Build a TokenCollectionRequest from a TokenDTO.
     *
     * Automatically handles ERC-1155 tokenId extraction from metadata.
     * Use this when you need more control over the request than
     * `getAccountOwnedTokensFromContract` provides.
     *
     * Works with any valid blockchain address - user wallets, external wallets, etc.
     *
     * @param accountAddress - Any valid blockchain address (wallet, contract, etc.)
     * @param token - Token definition
     * @param maxTokens - Maximum tokens to retrieve
     * @returns TokenCollectionRequest ready for getTokenCollection()
     *
     * @example
     * ```typescript
     * const request = sdk.web3.buildCollectionRequest(walletAddress, rewardToken);
     * const collection = await sdk.web3.getTokenCollection(request);
     * ```
     */
    buildCollectionRequest(accountAddress, token, maxTokens = 50) {
        return {
            accountAddress: accountAddress,
            contractAddress: token.contractAddress,
            abi: token.abi,
            chainId: token.chainId,
            tokenIds: token.type === NativeTokenTypes.ERC1155 ? this.extractTokenIds(token) : undefined,
            maxTokens
        };
    }
    /**
     * Cleanup resources.
     *
     * Call this when the SDK is being destroyed to clean up
     * event subscriptions and cached data.
     */
    destroy() {
        this._collectionManager?.destroy();
        this._balanceManager?.destroy();
        this._collectionManager = undefined;
        this._balanceManager = undefined;
    }
}

export { BalanceManager as B, IPFSInfrastructureApi as I, TokenCollectionManager as T, Web3Manager as W, Web3ApplicationService as a, Web3InfrastructureApi as b, getExplorerUrlByChainId as c, getExplorerUrl as g };
//# sourceMappingURL=web3-manager-B-IsluxI.js.map
